import { useState, useEffect, useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { Input } from "./input";
import { MapPin, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";

export interface BetterAddressPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  apiKey?: string;
}

const libraries: "places"[] = ["places"];

export function BetterAddressPicker({
  value = "",
  onChange,
  placeholder = "Search for an address...",
  className,
  disabled,
  apiKey,
}: BetterAddressPickerProps) {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [enrichedAddresses, setEnrichedAddresses] = useState<Record<string, string>>({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const placesContainerRef = useRef<HTMLDivElement | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      if (!placesContainerRef.current) {
        placesContainerRef.current = document.createElement("div");
      }
      placesServiceRef.current = new google.maps.places.PlacesService(placesContainerRef.current);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const buildFullAddress = (place: google.maps.places.PlaceResult): string => {
    const components = place.address_components || [];
    const get = (type: string) => components.find((c) => c.types.includes(type))?.long_name || "";
    const getShort = (type: string) =>
      components.find((c) => c.types.includes(type))?.short_name || "";

    const streetNumber = get("street_number");
    const route = get("route");
    const city = get("locality") || get("sublocality_level_1");
    const state = getShort("administrative_area_level_1");
    const zip = get("postal_code");

    const street = [streetNumber, route].filter(Boolean).join(" ");
    return [street, city, state, zip].filter(Boolean).join(", ");
  };

  const enrichPredictions = (items: google.maps.places.AutocompletePrediction[]) => {
    if (!placesServiceRef.current) return;

    for (const item of items) {
      placesServiceRef.current.getDetails(
        { placeId: item.place_id, fields: ["address_components"] },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const full = buildFullAddress(place);
            setEnrichedAddresses((prev) => ({ ...prev, [item.place_id]: full }));
          }
        },
      );
    }
  };

  const fetchPredictions = (input: string) => {
    if (!isLoaded || !autocompleteServiceRef.current || input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      autocompleteServiceRef.current?.getPlacePredictions(
        {
          input,
          types: ["address"],
          componentRestrictions: { country: "us" },
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
            setShowDropdown(true);
            setSelectedIndex(-1);
            enrichPredictions(predictions);
          } else {
            setPredictions([]);
            setShowDropdown(false);
          }
        },
      );
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);

    if (newValue) {
      fetchPredictions(newValue);
    } else {
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  const handleSelectPrediction = async (prediction: google.maps.places.AutocompletePrediction) => {
    setPredictions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);

    const enriched = enrichedAddresses[prediction.place_id];
    if (enriched) {
      setInputValue(enriched);
      onChange?.(enriched);
      return;
    }

    if (!placesServiceRef.current) {
      setInputValue(prediction.description);
      onChange?.(prediction.description);
      return;
    }

    placesServiceRef.current.getDetails(
      { placeId: prediction.place_id, fields: ["address_components"] },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          setInputValue(prediction.description);
          onChange?.(prediction.description);
          return;
        }

        const fullAddress = buildFullAddress(place);
        setInputValue(fullAddress);
        onChange?.(fullAddress);
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelectPrediction(predictions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange?.("");
    setPredictions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loadError) {
    return <Input className={cn(className)} value="Error loading Google Maps API" disabled />;
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) {
              setShowDropdown(true);
            }
          }}
          disabled={disabled || !isLoaded}
          placeholder={placeholder}
          className={cn("pr-8", !isLoaded && "opacity-70", className)}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-stone-500 flex items-center justify-center hover:bg-stone-100"
            tabIndex={-1}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-[200px] overflow-auto mt-1"
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectPrediction(prediction);
              }}
              className={cn(
                "w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2",
                selectedIndex === index && "bg-stone-50",
              )}
            >
              <MapPin className="h-4 w-4 text-stone-500 flex-shrink-0" />
              <span className="text-sm">
                {enrichedAddresses[prediction.place_id] || prediction.description}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Spacer below to make parent grow when dropdown is visible */}
      {showDropdown && predictions.length > 0 && <div className="h-[200px]" aria-hidden="true" />}
    </div>
  );
}
